import { Injectable, inject } from '@angular/core';
import { Observable, OperatorFunction, catchError, map, throwError } from 'rxjs';
import {
  clonagemToClonarCommand,
  filtroPlanosToQueryParams,
  opcoesInstitutosToPatchCommand,
  planoAlteracaoToPatchCommand,
  planoCadastroToCreateCommand,
  planoParamDtoToOpcoesInstitutos,
  planoParamDtoToPlanoDetalhe,
  planoParamDtoToPlanoResumo,
  planoProdutoDtoToProdutoPlano,
  planosParamsResponseToPaginaPlanos,
} from '../../mappers/parametros/plano-api.mapper';
import { EnumStatusResponse } from '../../enum/enumHttpResponse';
import {
  PaginaPlanos,
  PlanoAlteracao,
  PlanoCadastro,
  PlanoDetalhe,
  PlanoResumo,
  ProdutoPlano,
} from '../../models/parametros/plano.model';
import { OpcoesInstitutosPlano } from '../../models/parametros/plano-institutos.model';
import { FiltroPlanos } from '../../utils/parametros/planos.util';
import { ApiHttpError } from '../../../shared/services/http-client/api-http-error.model';
import { PlanosApiService } from './planos-api.service';

/** Final do nome da parametrização gerada ao duplicar um plano ("<nome_plano> - copia"). */
export const SUFIXO_COPIA = ' - copia';

/** Erro ao gravar quando o produto já tem parametrização (uma por produto; a API responde 409). */
export class CodigoPlanoEmUsoError extends Error {
  constructor(readonly codigo: string) {
    super(`O plano ${codigo} já está parametrizado.`);
    this.name = 'CodigoPlanoEmUsoError';
  }
}

/** Acesso aos planos parametrizados (`/v1/planos-param`) e aos produtos que podem recebê-los. */
@Injectable({
  providedIn: 'root',
})
export class PlanosService {
  private readonly planosApiService = inject(PlanosApiService);

  /** Página dos planos cadastrados (páginas começam em 1). */
  listarPlanos(filtro: FiltroPlanos, pagina: number, tamanhoPagina: number): Observable<PaginaPlanos> {
    return this.planosApiService
      .listarPlanosParams(filtroPlanosToQueryParams(filtro, pagina, tamanhoPagina))
      .pipe(map(planosParamsResponseToPaginaPlanos));
  }

  /** Dados completos do plano, para a edição. */
  obterPlano(id: number): Observable<PlanoDetalhe> {
    return this.planosApiService.obterPlanoParam(id).pipe(map(planoParamDtoToPlanoDetalhe));
  }

  /** Produtos vigentes, para o campo "Produto" do cadastro. */
  listarProdutos(): Observable<ProdutoPlano[]> {
    return this.planosApiService.listarProdutos().pipe(map((produtos) => produtos.map(planoProdutoDtoToProdutoPlano)));
  }

  /** Falha com `CodigoPlanoEmUsoError` se o código já existir. */
  criarPlano(dados: PlanoCadastro): Observable<PlanoDetalhe> {
    return this.planosApiService
      .criarPlanoParam(planoCadastroToCreateCommand(dados))
      .pipe(map(planoParamDtoToPlanoDetalhe), codigoEmUso(dados.produto.codigo));
  }

  atualizarPlano(id: number, dados: PlanoAlteracao): Observable<PlanoDetalhe> {
    return this.planosApiService
      .atualizarPlanoParam(id, planoAlteracaoToPatchCommand(dados))
      .pipe(map(planoParamDtoToPlanoDetalhe));
  }

  alterarStatus(id: number, status: PlanoResumo['status']): Observable<PlanoResumo> {
    return this.planosApiService
      .alterarStatusPlanoParam(id, { fl_ativo: status === 'Ativo' })
      .pipe(map(planoParamDtoToPlanoResumo));
  }

  /** Prazo de opção e combinação de institutos do plano (aba "Instituto"). */
  obterOpcoesInstitutos(id: number): Observable<OpcoesInstitutosPlano> {
    return this.planosApiService.obterPlanoParam(id).pipe(map(planoParamDtoToOpcoesInstitutos));
  }

  alterarOpcoesInstitutos(id: number, opcoes: OpcoesInstitutosPlano): Observable<OpcoesInstitutosPlano> {
    return this.planosApiService
      .atualizarPlanoParam(id, opcoesInstitutosToPatchCommand(opcoes))
      .pipe(map(planoParamDtoToOpcoesInstitutos));
  }

  /**
   * Copia o plano para o produto destino (ainda sem parametrização), com nome "<nome_plano> - copia"
   * e Inativa. Falha com `CodigoPlanoEmUsoError` se o destino já estiver parametrizado.
   */
  duplicarPlano(plano: PlanoResumo, destino: ProdutoPlano): Observable<PlanoResumo> {
    return this.planosApiService
      .clonarPlanoParam(plano.id, clonagemToClonarCommand(destino, `${plano.nome}${SUFIXO_COPIA}`))
      .pipe(map(planoParamDtoToPlanoResumo), codigoEmUso(destino.codigo));
  }
}

/** O back responde 409 quando o produto (o código do plano) já tem parametrização. */
function codigoEmUso<T>(codigo: string): OperatorFunction<T, T> {
  return catchError((erro: unknown) =>
    throwError(() =>
      erro instanceof ApiHttpError && erro.status === EnumStatusResponse.Conflict
        ? new CodigoPlanoEmUsoError(codigo.trim())
        : erro,
    ),
  );
}
